import { useRef, useMemo, useState, useEffect } from "react";
import { useTranslations, cx, currencyCodeToSymbol } from "@jugl-web/utils";
import { Popover, InteractiveContainer, PlainButton } from "../../primitives";
import { ListBox } from "../ListBox";
import { getCurrenciesList } from "./utils/getCurrenciesList";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { CurrencyPickerProps } from "./types";

export const CurrencyPicker = ({
  children,
  value,
  placement = "bottom",
  isDisabled,
  className,
  onSelect,
}: CurrencyPickerProps) => {
  const { t } = useTranslations();
  const [width, setWidth] = useState(264);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!wrapperRef.current) return;
    setWidth(Math.min(620, wrapperRef.current.offsetWidth));
  }, []);

  const currencyListItems = useMemo(
    () =>
      getCurrenciesList().map((item) => ({
        id: item.country_code,
        value: {
          code: item.code,
          label: item.name,
          flag: item.flag,
          symbol: item.symbol,
        },
      })),
    []
  );

  const selectedValue = useMemo(() => {
    if (!value) return undefined;
    const selectedItem = currencyListItems.find(
      (item) => item.value.code === value
    );
    return selectedItem ? [selectedItem.id] : undefined;
  }, [value, currencyListItems]);

  return (
    <div className="w-full">
      <Popover
        placement={placement}
        className={cx("p-6", className)}
        style={{ width }}
        isDisabled={isDisabled}
        renderTrigger={({ Trigger, triggerRef, isOpen }) => (
          <div ref={wrapperRef}>
            <Trigger ref={triggerRef} as={InteractiveContainer}>
              {typeof children === "function" ? children(isOpen) : children}
            </Trigger>
          </div>
        )}
      >
        {({ onClose }) => (
          <div className="flex flex-col gap-4">
            <span className="flex items-center justify-between font-semibold text-[#383838]">
              <div>
                {t({
                  id: "currency-picker-component.currency",
                  defaultMessage: "Currency",
                })}
              </div>
              <PlainButton onClick={onClose}>
                <CloseIcon />
              </PlainButton>
            </span>
            <ListBox
              selectionBehavior={{ mode: "single", canToggle: false }}
              onSelect={(item) => {
                onSelect(item.value);
                onClose();
              }}
              defaultSelectedIds={selectedValue}
              hasSearch
              hasSearchAutoFocus={false}
              renderSearchLabel={(item) =>
                `${item.value.label} ${item.value.code}`
              }
              items={currencyListItems}
              renderLabel={(item) => (
                <div className="flex flex-col font-secondary">
                  <div className="text-base text-dark">{item.id}</div>
                  <div className="text-xs text-[#828282] capitalize">
                    {item.value.label}
                  </div>
                </div>
              )}
              renderStartIcon={(item) => (
                <img
                  src={item.value.flag}
                  alt="flag"
                  loading="lazy"
                  className="h-[20px] w-[28px]"
                />
              )}
              renderEndIcon={(item) => currencyCodeToSymbol(item.value.code)}
              maxVisibleItems={5}
              itemSize="lg"
              spaceBetweenItems="compact"
            />
          </div>
        )}
      </Popover>
    </div>
  );
};
